import React, { useState } from 'react';
import { ClipboardList, Package, Minus, Plus, Search, Filter, Users, Layers, Eye, EyeOff, Briefcase, Map as MapIcon } from 'lucide-react';
import { PackageCard } from '../opportunities/PackageCard';
import { Job, PackageData, TeamMember } from './UnifiedMap';
import { PlayCircle, CheckCircle, AlertCircle, Clock, MapPin } from 'lucide-react';
interface UnifiedMapPanelProps {
  // Data
  jobs: Job[];
  packages: PackageData[];
  teamMembers: TeamMember[];
  // State
  activeTab: 'jobs' | 'opportunities';
  onTabChange: (tab: 'jobs' | 'opportunities') => void;
  // Selection
  activeJobId: number | null;
  activePackageId: number | null;
  onJobSelect: (id: number) => void;
  onPackageSelect: (id: number) => void;
  // Visibility
  layerVisibility: {
    jobs: boolean;
    opportunities: boolean;
    team: boolean;
  };
  onToggleLayer: (layer: 'jobs' | 'opportunities' | 'team') => void;
  // Panel State
  isCollapsed: boolean;
  onToggleCollapse: () => void;
}
export function UnifiedMapPanel({
  jobs,
  packages,
  teamMembers,
  activeTab,
  onTabChange,
  activeJobId,
  activePackageId,
  onJobSelect,
  onPackageSelect,
  layerVisibility,
  onToggleLayer,
  isCollapsed,
  onToggleCollapse
}: UnifiedMapPanelProps) {
  const [searchQuery, setSearchQuery] = useState('');
  // Filter data based on search
  const filteredJobs = jobs.filter(j => j.address.toLowerCase().includes(searchQuery.toLowerCase()) || j.cleaner.name.toLowerCase().includes(searchQuery.toLowerCase()));
  const filteredPackages = packages.filter(p => p.zoneName.toLowerCase().includes(searchQuery.toLowerCase()) || p.name.toLowerCase().includes(searchQuery.toLowerCase()));
  // Collapsed State
  if (isCollapsed) {
    return <div className="absolute top-4 left-4 z-[500] flex flex-col gap-2" data-id="element-2530">
        <button onClick={onToggleCollapse} className="flex items-center gap-2.5 bg-white rounded-xl border border-gray-200 px-4 py-3 hover:bg-gray-50 transition-all group" data-id="element-2531">
          <div className="w-8 h-8 rounded-lg bg-teal-deep/10 flex items-center justify-center" data-id="element-2532">
            <MapIcon size={16} className="text-teal-deep" data-id="element-2533" />
          </div>
          <div className="text-left" data-id="element-2534">
            <p className="text-sm font-semibold text-navy" data-id="element-2535">Mappa Operativa</p>
            <p className="text-[11px] text-gray-500" data-id="element-2536">
              {jobs.length} lavori · {packages.length} opp.
            </p>
          </div>
          <div className="ml-2 w-7 h-7 rounded-lg bg-gray-100 flex items-center justify-center group-hover:bg-teal-deep/10 transition-colors" data-id="element-2537">
            <Plus size={14} className="text-gray-500 group-hover:text-teal-deep" data-id="element-2538" />
          </div>
        </button>
      </div>;
  }
  // Expanded State
  return <div className="absolute top-4 left-4 z-[500] w-[380px] max-h-[calc(100%-32px)] flex flex-col bg-white rounded-xl border border-gray-200 overflow-hidden" data-id="element-2539">
      {/* Header */}
      <div className="p-4 border-b border-gray-200 bg-white" data-id="element-2540">
        <div className="flex items-center justify-between mb-4" data-id="element-2541">
          <h1 className="text-lg font-bold font-montserrat text-navy flex items-center gap-2" data-id="element-2542">
            <MapIcon size={18} className="text-teal-deep" data-id="element-2543" />
            Mappa Operativa
          </h1>
          <button onClick={onToggleCollapse} className="w-8 h-8 rounded-lg bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors" data-id="element-2544">
            <Minus size={14} className="text-gray-600" data-id="element-2545" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex p-1 bg-gray-100 rounded-lg mb-4" data-id="element-2546">
          <button onClick={() => onTabChange('jobs')} className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-md text-xs font-medium transition-all ${activeTab === 'jobs' ? 'bg-white text-navy shadow-sm' : 'text-gray-500 hover:text-navy'}`} data-id="element-2547">
            <ClipboardList size={14} data-id="element-2548" />
            Lavori ({jobs.length})
          </button>
          <button onClick={() => onTabChange('opportunities')} className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-md text-xs font-medium transition-all ${activeTab === 'opportunities' ? 'bg-white text-navy shadow-sm' : 'text-gray-500 hover:text-navy'}`} data-id="element-2549">
            <Package size={14} data-id="element-2550" />
            Opportunità ({packages.length})
          </button>
        </div>

        {/* Layer Toggles */}
        <div className="flex gap-2 mb-4 overflow-x-auto pb-1 no-scrollbar" data-id="element-2551">
          <LayerToggle label="Lavori" active={layerVisibility.jobs} onClick={() => onToggleLayer('jobs')} icon={<Briefcase size={12} data-id="element-2553" />} color="bg-blue-50 text-blue-700 border-blue-100" data-id="element-2552" />
          <LayerToggle label="Opportunità" active={layerVisibility.opportunities} onClick={() => onToggleLayer('opportunities')} icon={<Package size={12} data-id="element-2555" />} color="bg-teal-50 text-teal-700 border-teal-100" data-id="element-2554" />
          <LayerToggle label="Team" active={layerVisibility.team} onClick={() => onToggleLayer('team')} icon={<Users size={12} data-id="element-2557" />} color="bg-navy/10 text-navy border-navy/20" data-id="element-2556" />
        </div>

        {/* Search */}
        <div className="relative" data-id="element-2558">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" data-id="element-2559" />
          <input type="text" placeholder={activeTab === 'jobs' ? 'Cerca lavoro o cleaner...' : 'Cerca zona o pacchetto...'} value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full pl-9 pr-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-teal-deep focus:border-transparent outline-none" data-id="element-2560" />
        </div>
      </div>

      {/* List Content */}
      <div className="flex-1 overflow-y-auto p-3 space-y-3 bg-gray-50/50" data-id="element-2561">
        {activeTab === 'jobs' ? filteredJobs.length > 0 ? filteredJobs.map(job => <JobCard key={job.id} job={job} isActive={job.id === activeJobId} onClick={() => onJobSelect(job.id)} data-id="element-2562" />) : <EmptyState label="Nessun lavoro trovato" data-id="element-2563" /> : filteredPackages.length > 0 ? filteredPackages.map(pkg => <PackageCard key={pkg.id} pkg={pkg} isActive={pkg.id === activePackageId} onSelect={onPackageSelect} data-id="element-2564" />) : <EmptyState label="Nessuna opportunità trovata" data-id="element-2565" />}
      </div>
    </div>;
}
// --- Subcomponents ---
function LayerToggle({
  label,
  active,
  onClick,
  icon,
  color
}: any) {
  return <button onClick={onClick} className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-full border text-[10px] font-medium transition-all whitespace-nowrap ${active ? color : 'bg-white border-gray-200 text-gray-400 opacity-60 hover:opacity-100'}`} data-id="element-2566">
      {active ? <Eye size={12} data-id="element-2567" /> : <EyeOff size={12} data-id="element-2568" />}
      {icon}
      {label}
    </button>;
}
function EmptyState({
  label
}: {
  label: string;
}) {
  return <div className="text-center py-8 px-4" data-id="element-2569">
      <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-2" data-id="element-2570">
        <Filter className="text-gray-400 w-5 h-5" data-id="element-2571" />
      </div>
      <p className="text-xs text-gray-500" data-id="element-2572">{label}</p>
    </div>;
}
function JobCard({
  job,
  isActive,
  onClick
}: {
  job: Job;
  isActive: boolean;
  onClick: () => void;
}) {
  return <div onClick={onClick} className={`bg-white border rounded-xl p-3 cursor-pointer transition-all hover:shadow-md ${isActive ? 'border-teal-deep ring-1 ring-teal-deep shadow-md' : 'border-gray-200'}`} data-id="element-2573">
      <div className="flex justify-between items-start mb-2" data-id="element-2574">
        <span className="text-[10px] font-bold px-1.5 py-0.5 rounded border text-gray-600 bg-gray-50 border-gray-200" data-id="element-2575">
          {job.type}
        </span>
        <span className="text-xs font-bold text-navy" data-id="element-2576">{job.earnings}</span>
      </div>

      <div className="flex items-start gap-2 mb-2" data-id="element-2577">
        <MapPin size={14} className="text-gray-400 mt-0.5 flex-shrink-0" data-id="element-2578" />
        <div data-id="element-2579">
          <p className="text-xs font-bold text-navy leading-tight" data-id="element-2580">
            {job.address}
          </p>
          <p className="text-[10px] text-gray-500" data-id="element-2581">{job.city}</p>
        </div>
      </div>

      <div className="flex items-center gap-2 text-[11px] text-gray-600 bg-gray-50 px-2 py-1 rounded border border-gray-100 mb-3 w-fit" data-id="element-2582">
        <Clock size={12} className="text-gray-400" data-id="element-2583" />
        <span className="font-medium" data-id="element-2584">
          {job.timeStart} - {job.timeEnd}
        </span>
      </div>

      <div className="flex justify-between items-center pt-2 border-t border-gray-50" data-id="element-2585">
        <div className="flex items-center gap-1.5" data-id="element-2586">
          <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 ${job.cleaner.avatarColor || 'bg-navy text-white'}`} data-id="element-2587">
            <span className="text-[8px] font-bold" data-id="element-2588">{job.cleaner.initials}</span>
          </div>
          <span className="text-[10px] text-gray-500 truncate max-w-[80px]" data-id="element-2589">
            {job.cleaner.name}
          </span>
        </div>
        <JobStatusBadge status={job.status} data-id="element-2590" />
      </div>
    </div>;
}
function JobStatusBadge({
  status
}: {
  status: Job['status'];
}) {
  switch (status) {
    case 'in-progress':
      return <span className="flex items-center gap-1 text-[10px] font-medium text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded-full border border-blue-100" data-id="element-2591">
          <PlayCircle size={10} data-id="element-2592" /> In Corso
        </span>;
    case 'completed':
      return <span className="flex items-center gap-1 text-[10px] font-medium text-green-600 bg-green-50 px-1.5 py-0.5 rounded-full border border-green-100" data-id="element-2593">
          <CheckCircle size={10} data-id="element-2594" /> Completato
        </span>;
    case 'issue':
      return <span className="flex items-center gap-1 text-[10px] font-medium text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded-full border border-amber-100" data-id="element-2595">
          <AlertCircle size={10} data-id="element-2596" /> Problema
        </span>;
    default:
      return <span className="flex items-center gap-1 text-[10px] font-medium text-gray-600 bg-gray-100 px-1.5 py-0.5 rounded-full border border-gray-200" data-id="element-2597">
          <Clock size={10} data-id="element-2598" /> Programmato
        </span>;
  }
}